import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useMemo, useRef, useState } from 'react';
import { createContainPaintModifier, TColumn } from '@/utils/dnd-utils.ts';

import { cn } from '@/utils/utils.ts';
import { Badge } from '@/components/ui/badge.tsx';
import { ScrollArea } from '@/components/ui/scroll-area.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import {
  getLabelRole,
  getLabelStatus,
  isUserEligibleForAssignment,
} from '@/features/users/schemas/users.utils.ts';
import { TUserWithAccessControl } from '@/features/users/schemas/users.types.ts';
import {
  RuleAssignmentDesktopToolbar,
  useRuleAssignmentDesktopFilters,
} from './rule-assignment-management-desktop-toolbar.tsx';

const defaultCols: TColumn[] = [
  {
    id: 'available-users',
    title: 'Available users',
  },
  {
    id: 'associated-users',
    title: 'Associated users',
  },
];

function UserCard({ user, disabled }: { user: TUserWithAccessControl; disabled?: boolean }) {
  const { setNodeRef, attributes, listeners, isDragging } = useSortable({
    id: user.userId,
    data: {
      type: 'User',
      user,
    },
    disabled,
  });

  return (
    <div
      title={isDragging ? undefined : `${user.firstName} ${user.lastName} - ${user.role}`}
      ref={setNodeRef}
      {...(isDragging || disabled ? {} : { ...attributes, ...listeners })}
      className={cn(
        'relative flex items-center justify-between gap-2 rounded-lg bg-slate-100 px-4 py-2',
        disabled ? 'cursor-default opacity-50' : 'cursor-grab',
        isDragging
          ? 'border-primary border-2 border-dashed opacity-40'
          : !disabled && 'hover:ring-primary hover:ring-2 hover:ring-inset'
      )}
    >
      <span className="my-auto truncate select-none">
        {user.firstName} {user.lastName}
        {user.newlyAdded && (
          <span className="ml-1 inline-block size-2 rounded-full bg-blue-400"></span>
        )}
      </span>
      <div className="flex items-center gap-2">
        {user.status !== 'Active' && (
          <Badge variant={getLabelStatus(user.status)} className="shrink-0">
            {user.status}
          </Badge>
        )}
        <Badge variant={getLabelRole(user.role)} className="shrink-0">
          {user.role}
        </Badge>
        {!disabled && (
          <button>
            <svg viewBox="0 0 20 20" width="12">
              <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

function ColumnContainer({
  column,
  users,
  previewUser,
  disableIneligible,
}: {
  column: TColumn;
  users: TUserWithAccessControl[];
  previewUser?: TUserWithAccessControl | null;
  disableIneligible?: boolean;
}) {
  const usersIds = useMemo(() => {
    return users.map((user) => user.userId);
  }, [users]);

  const { setNodeRef } = useSortable({
    id: column.id,
    data: {
      type: 'Column',
      column,
    },
  });

  return (
    <div ref={setNodeRef} className="w-full rounded-lg bg-slate-50 p-2">
      {/* Column title */}
      <h3 className="rounded-sm bg-slate-100 p-2 select-none">
        <span className="rounded-md bg-slate-200 px-2 py-1">{users?.length}</span>{' '}
        {column.title}
      </h3>

      {/* Column basic container */}
      <ScrollArea className="mt-2 flex grow flex-col gap-4">
        <SortableContext strategy={verticalListSortingStrategy} items={usersIds}>
          <div className="flex h-[400px] flex-col gap-2">
            {previewUser && (
              <div className="border-primary flex items-center justify-between gap-2 rounded-lg border-2 border-dashed bg-slate-100 px-4 py-2 opacity-40">
                <span className="truncate select-none">
                  {previewUser.firstName} {previewUser.lastName}
                </span>
                <div className="flex items-center gap-2">
                  {previewUser.status !== 'Active' && (
                    <Badge variant={getLabelStatus(previewUser.status)} className="shrink-0">
                      {previewUser.status}
                    </Badge>
                  )}
                  <Badge variant={getLabelRole(previewUser.role)} className="shrink-0">
                    {previewUser.role}
                  </Badge>
                </div>
              </div>
            )}
            {users.map((user) => (
              <UserCard
                key={user.userId}
                user={user}
                disabled={disableIneligible && !isUserEligibleForAssignment(user)}
              />
            ))}
          </div>
        </SortableContext>
      </ScrollArea>
    </div>
  );
}

export function RuleAssignmentManagementDesktop({
  users,
  onMoveUser,
  isLoading,
}: {
  users: TUserWithAccessControl[];
  onMoveUser: (userId: string, targetColumnId: string) => void;
  isLoading: boolean;
}) {
  const [columns, setColumns] = useState<TColumn[]>(defaultCols);
  const columnsId = useMemo(() => columns.map((col) => col.id), [columns]);

  const [activeUser, setActiveUser] = useState<TUserWithAccessControl | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);
  const userFilters = useRuleAssignmentDesktopFilters(users);
  const containerRef = useRef<HTMLDivElement>(null);
  const containPaintModifier = useMemo(() => createContainPaintModifier(containerRef), []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (isLoading)
    return (
      <div className="grid h-full max-h-[470px] w-full grid-cols-2 gap-4">
        <Skeleton />
        <Skeleton />
      </div>
    );

  return (
    <div className="flex h-full flex-col gap-2">
      <RuleAssignmentDesktopToolbar filters={userFilters} />
      <div
        ref={containerRef}
        className="flex w-full overflow-hidden"
        style={{ contain: 'paint' }}
      >
        <DndContext
          sensors={sensors}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
        >
          <SortableContext items={columnsId}>
            <div className="grid flex-1 grid-cols-2 gap-4">
              {columns.map((col) => (
                <ColumnContainer
                  key={col.id}
                  column={col}
                  users={userFilters.filteredUsers.filter((user) => user.columnId === col.id)}
                  previewUser={
                    activeUser && dragOverColumnId === col.id && activeUser.columnId !== col.id
                      ? activeUser
                      : null
                  }
                  disableIneligible={col.id === 'available-users'}
                />
              ))}
            </div>
          </SortableContext>

          <DragOverlay modifiers={[containPaintModifier]}>
            {activeUser && <UserCard user={activeUser} />}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );

  function onDragStart(event: DragStartEvent) {
    if (event.active.data.current?.type === 'User') {
      setActiveUser(event.active.data.current.user);
    }
  }

  function onDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over || active.data.current?.type !== 'User') return;

    let targetColumnId: string | undefined;

    if (over.data.current?.type === 'Column') {
      targetColumnId = over.id as string;
    } else if (over.data.current?.type === 'User') {
      targetColumnId = (over.data.current.user as TUserWithAccessControl).columnId;
    }

    if (targetColumnId) {
      setDragOverColumnId(targetColumnId);
    }
  }

  function onDragCancel() {
    setDragOverColumnId(null);
    setActiveUser(null);
  }

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    const targetColumn = dragOverColumnId;
    setDragOverColumnId(null);
    setActiveUser(null);

    if (!over) return;
    if (active.id === over.id) return;

    if (active.data.current?.type === 'Column') {
      setColumns((columns) => {
        const activeColumnIndex = columns.findIndex((col) => col.id === active.id);
        const overColumnIndex = columns.findIndex((col) => col.id === over.id);
        return arrayMove(columns, activeColumnIndex, overColumnIndex);
      });
      return;
    }

    if (active.data.current?.type === 'User' && targetColumn) {
      onMoveUser(active.id as string, targetColumn);
    }
  }
}
